/**
 * 大六壬 · 应期
 *
 * 六壬断应期「诸家异说最多」，本模块实现一套**可复现的通行简法**（规则集中、便于日后替换）：
 *
 * 1. 三传俱空        → 事无实据，应期取**末传**之月（事之终），并注明恐迁延难准；
 * 2. 驿马入传且不空  → 主速应，取**马星值日**（自起课日顺数第一个马星地支之日）为速应日，
 *                      同时以**初传**之月为参考；
 * 3. 初传落空        → 空亡须待出空，取初传之**冲**支所在月（冲空则起）；
 * 4. 初传乘凶将      → 事初有阻、成于终，取**末传**之月；
 * 5. 其余            → 发用即事之初应，取**初传**之月。
 *
 * 月份换算用「建」：寅=正月、卯=二月…子=冬月、丑=腊月（`monthOfZhi`）。
 */
import { Lunar } from 'lunar-javascript';
import { displayMonth } from '../engine';
import { TIAN_JIANG_LUCK, chong, zhiIndex } from './constants';
/** 地支索引 → 农历月（1..12，正月=寅） */
export function monthOfZhi(zhiIdx) {
    return ((((zhiIdx - 2) % 12) + 12) % 12) + 1;
}
/** 自 from 起顺数，找到第一个「日支 = target」的日子 */
function nextDayOfZhi(from, target) {
    for (let i = 0; i <= 60; i += 1) {
        const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + i);
        const l = Lunar.fromDate(d);
        if (l.getDayZhiIndexExact() === target) {
            return { days: i, ganZhi: `${l.getDayGanExact()}${l.getDayZhiExact()}` };
        }
    }
    return { days: -1, ganZhi: '' };
}
/**
 * 推算应期。
 * @param result 已排好的大六壬盘面（至少含起课时间、三传、神煞）
 * @param from 起算日（默认取盘面的起课时间，保证历史重放结果一致）
 */
export function computeYingQi(result, from) {
    const start = from ?? new Date(result.time);
    const [first, , last] = result.chuan;
    const notes = [];
    const allKong = result.chuan.every((c) => c.kong);
    const maZhi = result.shenSha.find((s) => s.name === '驿马')?.value ?? null;
    const maChuan = maZhi ? result.chuan.find((c) => c.zhi === maZhi) : undefined;
    const maInChuan = !!maChuan;
    /** 马星可发力（入传且不落空）才算速应 */
    const maUsable = maInChuan && !maChuan.kong;
    let monthZhi = first?.zhiIndex ?? 0;
    let basis;
    let daysAhead = null;
    let dayGanZhi = null;
    if (allKong) {
        monthZhi = last?.zhiIndex ?? monthZhi;
        basis = '末传（事之终）';
        notes.push('三传俱空，谋事无实，应期恐迁延难准');
    }
    else if (maUsable) {
        basis = '初传（发用即应）';
        const hit = nextDayOfZhi(start, zhiIndex(maZhi));
        if (hit.days >= 0) {
            daysAhead = hit.days;
            dayGanZhi = hit.ganZhi;
        }
    }
    else if (first?.kong) {
        monthZhi = chong(first.zhiIndex);
        basis = '初传出空（空亡逢冲则起）';
        notes.push(`初传${first.zhi}落旬空，须待出空方应`);
    }
    else if ((TIAN_JIANG_LUCK[first?.tianJiang ?? ''] ?? 0) < 0) {
        monthZhi = last?.zhiIndex ?? monthZhi;
        basis = '末传（事之终）';
        notes.push(`初传乘${first?.tianJiang}，事初有阻，成于末传`);
    }
    else {
        basis = '初传（发用即应）';
    }
    // 马星备注统一追加（凡落入三传都值得说明，无论是否可发力）
    if (maInChuan) {
        notes.push(maUsable ? '驿马入传，主变动奔驰，应期偏速' : '驿马入传而逢空，动而不速');
    }
    const lunarMonth = monthOfZhi(monthZhi);
    const monthLabel = displayMonth(lunarMonth);
    let text = `应期：约应于农历${monthLabel}（取用：${basis}）。`;
    if (daysAhead !== null && dayGanZhi) {
        text +=
            daysAhead === 0
                ? `马星值日即在今日（${dayGanZhi}日），最为速应。`
                : `马星值日为 ${dayGanZhi} 日，约在 ${daysAhead} 日后，速应可验于此。`;
    }
    if (notes.length > 0)
        text += `${notes.join('；')}。`;
    return { basis, lunarMonth, monthLabel, daysAhead, dayGanZhi, notes, text };
}
//# sourceMappingURL=yingqi.js.map